/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { AnyAction } from 'redux';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { DEFAULT_RETRY_OPTIONS } from '../../../config';
import { Channel } from '../../../core/entities/Channel';
import { ServicesClientImpl } from '../../../core/impls/ServicesClientImpl';
import { Log } from '../../../core/logging/Log';
import { RetryMechanism } from '../../../core/retrymechanism/RetryMechanism';
import { RetryOptions } from '../../../core/retrymechanism/RetryOptions';
import { TChannels } from '../../../core/types/TChannels';
import { showTemporaryGuideIssueOverlay } from '../../overlay/temporaryguideissueoverlay/temporaryguideissueoverlay.reducers';
import {
    loadGridGuideChannelsAction,
    resetGridGuideRowsAction,
    setGridGuideOffsetAction,
    setIsChannelsServiceFailAction,
} from '../guide.actions';
import { loadGridGuideChannels } from '../guide.reducers';
import { setVerticalPositionAction } from '../guidehighlight/guidehighlight.actions';
import {
    startGuidePageScrollAction,
    startTurboScrollAction,
    stopGuidePageScrollAction,
    stopTurboScrollAction,
} from './turboscroll.actions';
import {
    START_GUIDE_PAGE_SCROLL,
    STOP_GUIDE_PAGE_SCROLL,
    START_TURBO_SCROLL,
    STOP_TURBO_SCROLL,
} from './turboscroll.types';

const TAG = 'TurboScroll';
const initialState: {
    isTurboScrollActive: boolean;
    isUpward: boolean;
    isGuidePageScrollActive: boolean;
    isGuidePageUpward: boolean;
} = {
    isTurboScrollActive: false,
    isUpward: false,
    isGuidePageScrollActive: false,
    isGuidePageUpward: false,
};

export const turboScrollReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case START_TURBO_SCROLL:
        case STOP_TURBO_SCROLL:
            return {
                ...state,
                isTurboScrollActive: payload.isTurboScrollActive,
                isUpward: payload.isUpward,
            };
        case START_GUIDE_PAGE_SCROLL:
        case STOP_GUIDE_PAGE_SCROLL:
            return {
                ...state,
                isGuidePageScrollActive: payload.isGuidePageScrollActive,
                isGuidePageUpward: payload.isGuidePageUpward,
            };
        default:
            return state;
    }
};

export const fetchAndUpdateChannels = () => (dispatch: Function, getState: Function) => {
    Diagnostics.info(TAG, 'inside -> fetchAndUpdateChannels ');
    const { isUpward, isGuidePageUpward, isGuidePageScrollActive } = getState().turboScroll;
    const { offset, channelsCount, LIMIT } = getState().gridGuide;
    const { channelsType } = getState().channelOptionsOverlay;
    const scrollUpward = isUpward || isGuidePageUpward;
    const limit = isGuidePageScrollActive ? LIMIT : 1;
    const currentOffset = scrollUpward
        ? (offset - limit + channelsCount) % channelsCount
        : (offset + limit) % channelsCount;

    dispatch(resetGridGuideRowsAction());
    const services: ServicesClientImpl = new ServicesClientImpl();
    const fetchChannels = services.fetchChannels.bind(services);
    RetryMechanism.callServiceWithRetry<TChannels>(
        fetchChannels,
        [channelsType, currentOffset, LIMIT],
        <RetryOptions>DEFAULT_RETRY_OPTIONS,
    )
        .then((response: TChannels) => {
            const stationIds = <string[]>response.channels.map((channel: Channel) => {
                return channel.stationId;
            });
            dispatch(
                loadGridGuideChannelsAction(
                    response.channels,
                    response.channelCount,
                    stationIds,
                    false,
                ),
            );
        })
        .catch((error) => {
            dispatch(setIsChannelsServiceFailAction(true));
            dispatch(showTemporaryGuideIssueOverlay());
            Diagnostics.error(
                TAG,
                'Failed to get channels.fetchAndUpdateChannels -> services.fetchChannels.',
            );
            Diagnostics.debug(TAG, error);
            Log.e(TAG, error);
        });

    dispatch(setGridGuideOffsetAction(currentOffset));
};

export const startTurboScroll =
    (isUpward: boolean = false) =>
    (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;
        if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS) return;
        Diagnostics.info(TAG, 'TurboScroll enabled');
        Log.i(TAG, 'TurboScroll enabled');
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.TURBO_SCROLL));
        dispatch(startTurboScrollAction(isUpward));
    };

export const stopTurboScroll = () => (dispatch: Function, getState: Function) => {
    const { gridGuide, screens } = getState();
    const { LIMIT } = gridGuide;
    const { screenTaskController } = screens;
    if (screenTaskController !== ScreenTaskControllerTypes.TURBO_SCROLL) return;
    Diagnostics.info(TAG, 'TurboScroll disabled');
    Log.i(TAG, 'TurboScroll disabled');
    dispatch(stopTurboScrollAction());
    dispatch(loadGridGuideChannels());
    dispatch(setVerticalPositionAction(LIMIT - 1));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS));
};

export const startGuidePageScroll =
    (isGuidePageUpward: boolean = false) =>
    (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;
        if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS) return;
        Diagnostics.info(TAG, 'PageScroll enabled');
        Log.i(TAG, 'PageScroll enabled');
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.TURBO_SCROLL));
        dispatch(startGuidePageScrollAction(isGuidePageUpward));
    };

export const stopGuidePageScroll = () => (dispatch: Function, getState: Function) => {
    const { gridGuide, screens } = getState();
    const { LIMIT } = gridGuide;
    const { screenTaskController } = screens;
    if (screenTaskController !== ScreenTaskControllerTypes.TURBO_SCROLL) return;
    Diagnostics.info(TAG, 'PageScroll disabled');
    Log.i(TAG, 'PageScroll disabled');
    dispatch(stopGuidePageScrollAction());
    dispatch(loadGridGuideChannels());
    dispatch(setVerticalPositionAction(LIMIT - 1));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS));
};

export default turboScrollReducer;
